<template>
  <div class="w-full overflow-hidden">
    <div v-if="isLoading">
      <LoadingScreen />
    </div>
    <Toast />
    <ClientOnly>
      <GlobalSearch v-if="searchInitialized" v-model:visible="searchEnabled" />
      <header class="site-header relative z-2 surface-0">
        <Menubar
          :model="menuItems"
          :pt="menubarPassThrough"
          :buttonProps="{ 'aria-label': 'Toggle primary navigation' }"
          breakpoint="1200px"
          aria-label="Primary navigation"
        >
          <template #start>
            <NuxtLink
              to="/"
              class="site-header-brand inline-flex align-items-center no-underline border-round-lg"
              aria-label="SATS home"
            >
              <Image
                src="/images/sats_menubar_logo.webp"
                alt="South African Theological Seminary"
                class="block"
                imageClass="block h-3rem w-auto"
              />
            </NuxtLink>
          </template>

          <template #item="{ item, props, hasSubmenu, root }">
            <NuxtLink
              v-if="item.route || item.url"
              :to="item.route || item.url"
              :external="Boolean(item.url)"
              v-bind="props.action"
              :target="item.url ? '_blank' : undefined"
              :rel="item.url ? 'noopener noreferrer' : undefined"
              @mousedown.prevent
              @mousemove="stopNestedMenuItemMouseMove($event, root)"
            >
              <span v-bind="props.label">{{ item.label }}</span>
              <i
                v-if="hasSubmenu"
                v-bind="props.submenuicon"
                :class="[props.submenuicon.class, 'pi', root ? 'pi-angle-down' : 'pi-angle-right']"
                aria-hidden="true"
              ></i>
            </NuxtLink>
            <a
              v-else
              href="#"
              v-bind="props.action"
              @click.prevent
              @mousedown.prevent
              @mousemove="stopNestedMenuItemMouseMove($event, root)"
            >
              <span v-bind="props.label">{{ item.label }}</span>
              <i
                v-if="hasSubmenu"
                v-bind="props.submenuicon"
                :class="[props.submenuicon.class, 'pi', root ? 'pi-angle-down' : 'pi-angle-right']"
                aria-hidden="true"
              ></i>
            </a>
          </template>

          <template #end>
            <Button
              text
              rounded
              icon="pi pi-search"
              aria-label="Search SATS"
              aria-haspopup="dialog"
              :pt="menubarSearchButtonPassThrough"
              @click="openSearch"
            />
          </template>
        </Menubar>
      </header>
    </ClientOnly>

    <slot />

    <footer class="site-footer surface-0">
      <div class="footer-main">
        <div class="footer-container w-full mx-auto">
          <div class="footer-intro flex justify-content-between">
            <NuxtLink
              to="/"
              class="footer-brand inline-flex border-round-lg"
              aria-label="SATS home"
            >
              <Image
                src="/images/sats_menubar_logo.webp"
                alt="South African Theological Seminary"
                class="block w-full"
                imageClass="block w-full h-auto"
              />
            </NuxtLink>

            <div class="footer-callout flex">
              <div>
                <p class="footer-callout-label m-0 font-semibold">Ready for your next step?</p>
                <p class="footer-callout-text m-0">Begin your study journey with SATS.</p>
              </div>
              <NuxtLink
                to="https://sats.nexsisportal.com/login"
                external
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button rounded label="Apply online" class="border-none" />
              </NuxtLink>
            </div>
          </div>

          <Divider class="footer-divider mx-0 mb-5" />

          <div class="footer-grid align-items-stretch">
            <Card
              v-for="section in footerSections"
              :key="section.id"
              class="footer-section"
              :pt="footerCardPassThrough"
            >
              <template #content>
                <nav :aria-labelledby="`footer-${section.id}-title`">
                  <h2 :id="`footer-${section.id}-title`" class="footer-section-title font-semibold">
                    {{ section.title }}
                  </h2>
                  <ul class="footer-link-list list-none p-0 m-0">
                    <li v-for="link in section.links" :key="link.href">
                      <NuxtLink
                        :to="link.href"
                        :external="link.external"
                        :target="link.external ? '_blank' : undefined"
                        :rel="link.external ? 'noopener noreferrer' : undefined"
                        class="footer-link inline-flex no-underline"
                      >
                        {{ link.label }}
                      </NuxtLink>
                    </li>
                  </ul>
                </nav>
              </template>
            </Card>

            <Card class="footer-section" :pt="footerCardPassThrough">
              <template #content>
                <section aria-labelledby="footer-help-title">
                  <h2 id="footer-help-title" class="footer-section-title font-semibold">
                    How can we help?
                  </h2>
                  <div class="footer-contact-list">
                    <NuxtLink
                      to="tel:+27110224440"
                      external
                      class="footer-contact-link no-underline align-items-center border-round-xl"
                    >
                      <Avatar
                        shape="circle"
                        class="flex-shrink-0"
                        :pt="footerContactAvatarPassThrough"
                        aria-hidden="true"
                      >
                        <template #icon>
                          <i class="pi pi-phone"></i>
                        </template>
                      </Avatar>
                      <span>
                        <span class="footer-contact-label block font-medium uppercase"
                          >Helpdesk</span
                        >
                        <strong class="block font-semibold">+27 11 022-4440</strong>
                      </span>
                    </NuxtLink>

                    <NuxtLink
                      to="mailto:info@sats.ac.za"
                      external
                      class="footer-contact-link no-underline align-items-center border-round-xl"
                    >
                      <Avatar
                        shape="circle"
                        class="flex-shrink-0"
                        :pt="footerContactAvatarPassThrough"
                        aria-hidden="true"
                      >
                        <template #icon>
                          <i class="pi pi-envelope"></i>
                        </template>
                      </Avatar>
                      <span>
                        <span class="footer-contact-label block font-medium uppercase">
                          General queries
                        </span>
                        <strong class="block font-semibold">info@sats.ac.za</strong>
                      </span>
                    </NuxtLink>

                    <NuxtLink
                      to="https://tawk.to/chat/5891ad1e34fec90a6ab3e0d9/default?$_tawk_sk=5e8d8cad7e637bd3e67403be&amp;$_tawk_tk=844c87e5e28ce00d4491f6a010dfa19b&amp;v=682"
                      external
                      target="_blank"
                      rel="noopener noreferrer"
                      class="footer-contact-link no-underline align-items-center border-round-xl"
                    >
                      <Avatar
                        shape="circle"
                        class="flex-shrink-0"
                        :pt="footerContactAvatarPassThrough"
                        aria-hidden="true"
                      >
                        <template #icon>
                          <i class="pi pi-comments"></i>
                        </template>
                      </Avatar>
                      <span>
                        <span class="footer-contact-label block font-medium uppercase">
                          Need more help?
                        </span>
                        <strong class="block font-semibold">Let's talk</strong>
                      </span>
                    </NuxtLink>
                  </div>
                </section>
              </template>
            </Card>

            <Card class="footer-section" :pt="footerCardPassThrough">
              <template #content>
                <section aria-labelledby="footer-banking-title">
                  <h2 id="footer-banking-title" class="footer-section-title font-semibold">
                    Banking details
                  </h2>
                  <DataTable
                    :value="bankingDetails"
                    dataKey="label"
                    size="small"
                    class="footer-bank-table w-full"
                    :pt="footerBankTablePassThrough"
                    :tableProps="{ 'aria-labelledby': 'footer-banking-title' }"
                  >
                    <Column
                      field="label"
                      header="Detail"
                      style="width: 44%"
                      bodyClass="footer-bank-label pr-3"
                    />
                    <Column
                      field="value"
                      header="Information"
                      style="width: 56%"
                      bodyClass="footer-bank-value font-semibold text-right"
                    />
                  </DataTable>
                </section>
              </template>
            </Card>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <div
          class="footer-container footer-bottom-content w-full mx-auto flex justify-content-between"
        >
          <p class="m-0 line-height-3">
            &copy; {{ currentYear }} South African Theological Seminary. All rights reserved.
          </p>
          <nav class="md:mr-8" aria-label="SATS social media">
            <ul class="footer-social-list list-none p-0 m-0 flex">
              <li v-for="socialLink in socialLinks" :key="socialLink.label">
                <NuxtLink
                  :to="socialLink.href"
                  external
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="socialLink.label"
                >
                  <Button
                    rounded
                    text
                    :icon="socialLink.icon"
                    class="footer-social-link p-0"
                    :aria-label="socialLink.label"
                  />
                </NuxtLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
let isLoading = ref(true)

const router = useRouter()
const searchEnabled = ref(false)
const searchInitialized = ref(false)
let removeMenuFocusGuard: (() => void) | undefined

const menubarPassThrough = {
  root: {
    class: 'site-menubar surface-0 border-none border-noround px-3 md:px-4 w-full my-0 mx-auto',
  },
  start: {
    class: 'flex align-items-center flex-shrink-0 flex-order-0 mr-2',
  },
  menu: {
    class: 'site-menubar-list gap-1 flex-nowrap',
  },
  content: {
    class: 'site-menu-content border-round-lg relative',
  },
  action: {
    class: 'site-menu-action no-underline',
  },
  label: {
    class: 'site-menu-label font-medium',
  },
  submenuIcon: {
    class: 'site-menu-submenu-icon',
  },
  submenu: {
    class: 'site-menu-submenu border-round-xl shadow-2',
  },
  end: {
    class: 'site-menubar-actions flex align-items-center flex-shrink-0 flex-order-2 ml-auto',
  },
  button: {
    class: 'site-menubar-toggle flex-shrink-0 flex-order-3 ml-2',
  },
  menubuttonicon: {
    class: 'text-lg',
  },
}

const menubarSearchButtonPassThrough = {
  root: {
    class: 'site-menu-search-button flex-shrink-0',
  },
  icon: {
    class: 'text-base',
  },
}

interface Item {
  name: string
  link: string
}

interface ProgrammeItem extends Item {
  web_programme_category_id: number | null
}

interface ProgrammeCategory {
  id: number
  menu_label: string | null
  name: string
  slug: string
}

interface FooterLink {
  label: string
  href: string
  external?: boolean
}

interface FooterSection {
  id: string
  title: string
  links: FooterLink[]
}

const footerSections: FooterSection[] = [
  {
    id: 'studies',
    title: 'Studies',
    links: [
      {
        label: 'Apply Online',
        href: 'https://sats.nexsisportal.com/login',
        external: true,
      },
      { label: 'Course Outcomes', href: '/resources/courses' },
      { label: 'Graduation', href: '/graduation' },
      { label: 'Recognition of Prior Learning', href: '/recognition-of-prior-learning' },
      { label: 'Study Options', href: '/programmes' },
      { label: 'Undergraduate', href: '/programmes/undergraduate' },
      { label: 'Postgraduate', href: '/programmes/postgraduate' },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    links: [
      { label: 'Prospectus', href: '/resources/prospectus' },
      { label: 'Course Descriptions', href: '/resources/courses' },
      { label: 'Conspectus', href: '/resources/conspectus' },
      { label: 'Student Research', href: '/resources/student-research' },
      { label: 'Live Events', href: '/resources/live-events' },
      { label: 'Blogs', href: '/resources/blogs' },
      { label: 'Videos', href: '/resources/videos' },
      { label: 'Newsletters', href: '/resources/newsletters' },
    ],
  },
]

const bankingDetails = [
  { label: 'Bank', value: 'First National' },
  { label: 'Branch', value: 'Fourways Mall' },
  { label: 'Branch number', value: '251655' },
  { label: 'Account name', value: 'SA Theological Seminary' },
  { label: 'Account number', value: '51321182366' },
  { label: 'SWIFT code', value: 'FIRNZAJJ' },
]

const socialLinks = [
  {
    label: 'Follow SATS on Facebook',
    href: 'https://www.facebook.com/SATSonline',
    icon: 'pi pi-facebook',
  },
  {
    label: 'Follow SATS on X',
    href: 'https://twitter.com/satsedu',
    icon: 'pi pi-twitter',
  },
  {
    label: 'Watch SATS on YouTube',
    href: 'https://www.youtube.com/user/SATSeminary',
    icon: 'pi pi-youtube',
  },
  {
    label: 'Subscribe to the SATS RSS feed',
    href: 'http://feeds.feedburner.com/SATS',
    icon: 'pi pi-link',
  },
]

const currentYear = new Date().getFullYear()

const footerCardPassThrough = {
  root: {
    class: 'min-w-0 border-1 border-round-2xl',
    style: {
      color: 'var(--sats-ink)',
      borderColor: 'var(--sats-border)',
      boxShadow: '0 0.4rem 1.4rem rgba(28, 59, 48, 0.045)',
    },
  },
  body: { class: 'p-4 h-full' },
  content: { class: 'p-0' },
}

const footerContactAvatarPassThrough = {
  root: {
    class: 'bg-primary-50 text-primary',
    style: {
      width: '2.35rem',
      height: '2.35rem',
    },
  },
}

const footerBankTablePassThrough = {
  root: { class: 'bg-transparent', style: { color: 'var(--sats-ink)' } },
  wrapper: { class: 'bg-transparent' },
  table: { class: 'bg-transparent' },
  thead: { class: 'hidden' },
  bodyRow: { class: 'bg-transparent', style: { color: 'var(--sats-ink)' } },
}

const programmesFilter = {
  is_landing: false,
  is_on_website: true,
}
const programmesRequest = useAsyncData<ProgrammeItem[]>(
  `${JSON.stringify(programmesFilter)}-programmes`,
  () => {
    return queryContent('programmes')
      .where(programmesFilter)
      .sort({ row_order: 1, $numeric: true })
      .only(['name', 'link', 'web_programme_category_id'])
      .find()
  }
)

const programmeCategoriesRequest = useAsyncData<ProgrammeCategory[]>(
  'web-programme-categories',
  () => {
    return queryContent('web_programme_category')
      .sort({ row_order: 1, $numeric: true })
      .only(['id', 'menu_label', 'name', 'slug'])
      .find()
  }
)

const [{ data: programmes }, { data: programmeCategories }] = await Promise.all([
  programmesRequest,
  programmeCategoriesRequest,
])

// const resourcesFilter = {
//   parent_id: 2,
// }
// const { data: resources } = await useAsyncData<Item[]>(
//   `${JSON.stringify(resourcesFilter)}-resources`,
//   () => {
//     return queryContent('resources').where(resourcesFilter).only(['name', 'link']).find()
//   }
// )

const openSearch = () => {
  searchInitialized.value = true
  searchEnabled.value = true
}

const stopNestedMenuItemMouseMove = (event: MouseEvent, isRootItem: boolean) => {
  // PrimeVue 3.53 otherwise applies the nested item's index to the root focus state.
  if (!isRootItem) {
    event.stopPropagation()
  }
}

const releaseMenuFocus = () => {
  const activeElement = document.activeElement

  if (activeElement instanceof HTMLElement && activeElement.closest('.site-menubar')) {
    activeElement.blur()
  }
}

onMounted(() => {
  isLoading.value = false
  removeMenuFocusGuard = router.beforeEach(() => {
    releaseMenuFocus()
  })
})

onBeforeUnmount(() => {
  removeMenuFocusGuard?.()
})

const menuItems = computed(() => {
  const programmeCategoryItems = (programmeCategories.value ?? []).map((category) => ({
    label: category.menu_label?.trim() || category.name,
    items: [
      {
        label: 'Faculty',
        route: `/faculty/${category.slug}`,
      },
      ...(programmes.value ?? [])
        .filter((programme) => programme.web_programme_category_id === category.id)
        .map((programme) => ({
          label: programme.name,
          route: programme.link,
        })),
    ],
  }))

  // const resourceItems =
  //   resources.value.map((p) => ({
  //     label: p.name,
  //     route: p.link,
  //   })) ?? []

  return [
    {
      label: 'Programmes',
      class: 'programmes-menu-item',
      items: [
        {
          label: 'Overview',
          route: '/programmes',
        },
        ...programmeCategoryItems,
        {
          label: 'Study Options',
          items: [
            { label: 'Prospectus', route: '/resources/prospectus' },
            { label: 'Stand Alone Courses', route: '/programmes/stand-alone-courses' },
            { label: 'Micro Seminary', route: '/micro-seminary' },
          ],
        },
      ],
    },
    {
      label: 'Resources',
      items: [
        {
          label: 'Prospectus',
          route: '/resources/prospectus',
        },
        {
          label: 'Course Descriptions',
          route: '/resources/courses',
        },
        {
          label: 'Conspectus',
          route: '/resources/conspectus',
        },
        {
          label: 'Student Research',
          route: '/resources/student-research',
        },
        {
          label: 'Live Events',
          route: '/resources/live-events',
        },
        {
          label: 'Blogs',
          route: '/resources/blogs',
        },
        {
          label: 'Videos',
          route: '/resources/videos',
        },
        {
          label: 'Newsletters',
          route: '/resources/newsletters',
        },
      ],
    },
    { label: 'Accreditation', route: '/accreditation' },
    { label: 'Pricing', route: '/pricing' },
    { label: 'About us', route: '/about-us' },
    { label: 'Staff', route: '/staff' },
    { label: 'Contact Us', route: '/contact-us' },
    { label: 'Micro Seminary', route: '/micro-seminary', class: 'micro-button' },
    { label: 'MySats', url: 'https://mysats.nexsiscampus.com/', class: 'mysats-button' },
    { label: 'Apply', url: 'https://sats.nexsisportal.com/login', class: 'apply-button' },
  ]
})
</script>

<style lang="scss">
.site-header {
  color: var(--sats-ink);
  border-bottom: 1px solid var(--sats-border);
  box-shadow: 0 0.25rem 1rem rgba(28, 59, 48, 0.045);
}

.site-menubar.p-menubar {
  max-width: 86rem;
  min-height: 5rem;
  color: var(--sats-ink);
}

.site-menubar .site-menu-content {
  background: transparent;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;
}

.site-menubar.p-menubar .p-menuitem > .site-menu-content .site-menu-action {
  gap: 0.4rem;
  padding: 0.72rem 0.62rem;
  color: var(--sats-muted-strong);
  border-radius: inherit;
}

.site-menubar.p-menubar .p-menuitem > .site-menu-content .site-menu-action .site-menu-label {
  color: var(--sats-muted-strong);
  font-size: 0.78rem;
  line-height: 1.25;
  white-space: nowrap;
}

.site-menubar.p-menubar .p-menuitem > .site-menu-content .site-menu-action .site-menu-submenu-icon {
  color: #718079;
  font-size: 0.72rem;
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.site-menubar.p-menubar .p-menuitem:not(.p-highlight):not(.p-disabled) > .site-menu-content:hover,
.site-menubar.p-menubar .p-menuitem.p-highlight > .site-menu-content {
  color: var(--sats-green);
  background: rgba(var(--sats-green-rgb), 0.075);
}

.site-menubar.p-menubar .p-menuitem:not(.p-disabled) > .site-menu-content:hover .site-menu-label,
.site-menubar.p-menubar
  .p-menuitem:not(.p-disabled)
  > .site-menu-content:hover
  .site-menu-submenu-icon,
.site-menubar.p-menubar .p-menuitem.p-highlight > .site-menu-content .site-menu-label,
.site-menubar.p-menubar .p-menuitem.p-highlight > .site-menu-content .site-menu-submenu-icon {
  color: var(--sats-green);
}

.site-menubar.p-menubar .p-menuitem.p-focus > .site-menu-content {
  color: var(--sats-green);
  background: rgba(var(--sats-green-rgb), 0.075);
}

.site-menubar.p-menubar .p-menuitem.p-focus > .site-menu-content .site-menu-label,
.site-menubar.p-menubar .p-menuitem.p-focus > .site-menu-content .site-menu-submenu-icon {
  color: var(--sats-green);
}

.site-menubar.p-menubar .site-menu-submenu {
  min-width: 14rem;
  padding: 0.4rem;
  color: var(--sats-ink);
  background: #ffffff;
  border: 1px solid var(--sats-border);
}

.site-menubar.p-menubar .site-menu-submenu .p-menuitem > .site-menu-content .site-menu-action {
  padding: 0.68rem 0.8rem;
}

.site-menubar.p-menubar
  .site-menu-submenu
  .p-menuitem
  > .site-menu-content
  .site-menu-action
  .site-menu-label {
  font-size: 0.8rem;
  line-height: 1.35;
  white-space: normal;
}

.site-menubar .micro-button {
  --site-menu-action-background: rgb(164, 208, 183);
  --site-menu-action-color: var(--sats-green);
}

.site-menubar .mysats-button {
  --site-menu-action-background: var(--sats-purple);
  --site-menu-action-color: #ffffff;
}

.site-menubar .apply-button {
  --site-menu-action-background: var(--sats-gold);
  --site-menu-action-color: #ffffff;
}

.site-menubar.p-menubar
  .site-menubar-list
  > .p-menuitem:is(.micro-button, .mysats-button, .apply-button)
  > .site-menu-content,
.site-menubar.p-menubar
  .site-menubar-list
  > .p-menuitem:is(.micro-button, .mysats-button, .apply-button)
  > .site-menu-content:hover {
  background: var(--site-menu-action-background);
  border: 0;
  box-shadow: none;
}

.site-menubar.p-menubar
  .site-menubar-list
  > .p-menuitem:is(.micro-button, .mysats-button, .apply-button)
  > .site-menu-content
  .site-menu-label,
.site-menubar.p-menubar
  .site-menubar-list
  > .p-menuitem:is(.micro-button, .mysats-button, .apply-button)
  > .site-menu-content:hover
  .site-menu-label {
  color: var(--site-menu-action-color);
}

.site-menubar.p-menubar
  .site-menubar-list
  > .p-menuitem:is(.micro-button, .mysats-button, .apply-button):is(:hover, .p-focus)
  > .site-menu-content {
  filter: brightness(0.94);
}

.site-menu-search-button.p-button.p-button-text {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--sats-green) !important;
  background: rgba(var(--sats-green-rgb), 0.07) !important;
  border: 1px solid rgba(var(--sats-green-rgb), 0.13) !important;
  box-shadow: none;
  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.site-header .site-menu-search-button.p-button:hover {
  color: #ffffff !important;
  background: var(--sats-green) !important;
  border-color: var(--sats-green) !important;
}

.site-header-brand:focus-visible,
.site-header .site-menu-search-button.p-button:focus-visible,
.site-menubar.p-menubar-mobile .site-menubar-toggle:focus-visible {
  outline: 0.2rem solid rgba(var(--sats-gold-rgb), 0.75);
  outline-offset: 0.2rem;
  box-shadow: none !important;
}

.site-menubar.p-menubar-mobile {
  min-height: 4.5rem;
}

.site-menubar.p-menubar-mobile .site-menubar-toggle {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--sats-green);
  background: rgba(var(--sats-green-rgb), 0.07);
  border: 1px solid rgba(var(--sats-green-rgb), 0.13);
}

.site-menubar.p-menubar-mobile .site-menubar-toggle:hover {
  color: #ffffff;
  background: var(--sats-green);
}

.site-menubar.p-menubar-mobile .site-menubar-list {
  top: calc(100% + 0.5rem);
  right: 1rem;
  left: 1rem;
  width: auto;
  max-height: calc(100dvh - 6rem);
  padding: 0.5rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #ffffff;
  border: 1px solid var(--sats-border);
  border-radius: 1rem;
  box-shadow: 0 0.7rem 1.8rem rgba(28, 59, 48, 0.14);
}

.site-menubar.p-menubar.p-menubar-mobile .p-menuitem > .site-menu-content .site-menu-action {
  width: 100%;
  padding: 0.75rem 1rem;
}

.site-menubar.p-menubar.p-menubar-mobile
  .p-menuitem
  > .site-menu-content
  .site-menu-action
  .site-menu-label {
  font-size: 0.85rem;
}

.site-menubar.p-menubar-mobile .site-menu-submenu {
  width: calc(100% - 1rem);
  margin: 0.25rem 0 0.35rem 1rem;
  padding: 0.25rem;
  background: rgba(var(--sats-green-rgb), 0.035);
  border: 0;
  border-left: 0.2rem solid rgba(var(--sats-green-rgb), 0.2);
  box-shadow: none !important;
}

.site-menubar.p-menubar.p-menubar-mobile
  .site-menu-submenu
  .p-menuitem
  > .site-menu-content
  .site-menu-action {
  padding: 0.65rem 0.85rem;
}

.site-footer {
  color: var(--sats-ink);
  border-top: 1px solid var(--sats-border);
}

.site-footer i {
  color: inherit;
}

.footer-main {
  padding: 3.5rem 1.5rem 3rem;
}

.footer-container {
  max-width: 86rem;
}

.footer-intro {
  align-items: center;
  gap: 2rem;
}

.footer-divider.p-divider-horizontal {
  margin-top: 2.25rem;
}

.footer-divider.p-divider-horizontal::before {
  border-top-color: var(--sats-border);
}

.footer-brand {
  width: min(18rem, 100%);
}

.footer-callout {
  align-items: center;
  gap: 1.5rem;
}

.footer-callout-label {
  color: var(--sats-ink);
  font-size: 1.05rem;
}

.footer-callout-text {
  margin-top: 0.2rem;
  color: var(--sats-muted);
  font-size: 0.9rem;
}

.footer-grid {
  display: grid;
  grid-template-columns: 0.9fr 0.9fr 1.15fr 1.35fr;
  gap: 1rem;
}

.footer-section-title {
  margin: 0 0 1.35rem;
  color: var(--sats-ink);
  font-size: 1.1rem;
  line-height: 1.3;
}

.footer-section-title::after {
  display: block;
  width: 2rem;
  height: 0.15rem;
  margin-top: 0.65rem;
  background: var(--sats-gold);
  border-radius: 999px;
  content: '';
}

.footer-link-list {
  display: grid;
  gap: 0.15rem;
}

.footer-link {
  padding: 0.35rem 0;
  color: var(--sats-muted);
  font-size: 0.9rem;
  line-height: 1.45;
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.footer-link:hover {
  color: var(--sats-green);
  transform: translateX(0.15rem);
}

.footer-contact-list {
  display: grid;
  gap: 0.35rem;
}

.footer-contact-link {
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.55rem;
  margin: 0 -0.55rem;
  color: var(--sats-ink);
  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

.footer-contact-link:hover {
  color: var(--sats-green);
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.96),
    rgba(var(--sats-green-rgb), 0.055)
  );
}

.footer-contact-label,
.footer-contact-link strong {
  overflow-wrap: anywhere;
}

.footer-contact-label {
  margin-bottom: 0.15rem;
  color: var(--sats-muted);
  font-size: 0.72rem;
  letter-spacing: 0.025em;
}

.footer-contact-link strong {
  font-size: 0.88rem;
  line-height: 1.4;
}

.footer-bank-table.p-datatable.p-datatable-sm .p-datatable-tbody > tr > td {
  padding: 0.55rem 0;
  border-color: rgba(var(--sats-green-rgb), 0.1);
}

.footer-bank-table.p-datatable.p-datatable-sm .p-datatable-tbody > tr:first-child > td {
  padding-top: 0;
}

.footer-bank-table.p-datatable.p-datatable-sm .p-datatable-tbody > tr:last-child > td {
  padding-bottom: 0;
  border-bottom: 0;
}

.footer-bank-label {
  color: var(--sats-muted);
  font-size: 0.78rem;
  line-height: 1.45;
}

.footer-bank-value {
  color: var(--sats-ink);
  font-size: 0.82rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.footer-bottom {
  padding: 1.2rem 1.5rem;
  color: rgba(255, 255, 255, 0.76);
  background: var(--sats-green-dark);
}

.footer-bottom-content {
  align-items: center;
  gap: 1.5rem;
}

.footer-bottom p {
  font-size: 0.78rem;
}

.footer-social-list {
  gap: 0.55rem;
}

.footer-social-link.p-button {
  width: 2.35rem;
  height: 2.35rem;
  color: #ffffff !important;
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.22);
  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.site-footer .footer-social-link.p-button:hover {
  color: var(--sats-green-dark) !important;
  background: var(--sats-gold) !important;
  border-color: var(--sats-gold) !important;
  transform: translateY(-2px);
}

.site-footer a:focus-visible {
  outline: 0.2rem solid var(--sats-green);
  outline-offset: 0.2rem;
}

.footer-bottom a:focus-visible {
  outline-color: #e0bd64;
}

@media screen and (max-width: 1120px) {
  .footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 700px) {
  .footer-main {
    padding: 2.5rem 1rem 2rem;
  }

  .footer-intro {
    align-items: flex-start;
    flex-direction: column;
    gap: 1.5rem;
  }

  .footer-divider.p-divider-horizontal {
    margin-top: 1.75rem;
  }

  .footer-callout {
    justify-content: space-between;
    width: 100%;
  }

  .footer-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .footer-section.p-card .p-card-body {
    padding: 1.25rem !important;
  }

  .footer-bottom {
    padding: 1.5rem 1rem;
  }

  .footer-bottom-content {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
    padding-right: 4.5rem;
  }
}

@media screen and (max-width: 480px) {
  .footer-callout {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-menubar .site-menu-content,
  .site-menubar .site-menu-submenu-icon,
  .site-menu-search-button.p-button,
  .site-menubar-toggle {
    transition: none !important;
  }

  .footer-link,
  .footer-contact-link,
  .footer-social-link {
    transition: none;
  }
}
</style>
